import React from 'react'
import { t } from '@lingui/core/macro'
import Tooltip from '@mui/material/Tooltip'
import { withStyles } from '@mui/styles'
import { colors } from '@global/constants'

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip)

const RootTemplate = props => (
  <div style={{ position: 'relative', paddingRight: '32px', width: '100%' }}>
    <HtmlTooltip
      style={{ backgroundColor: 'white' }}
      title={
        <div className="statusPopover validationTooltip arrow-top">
          <h3>{props.fn.getTooltipHeader(props.result.json_body.summary)}</h3>

          <p>{props.fn.getTooltipDescription(props.result.json_body.summary)}</p>

          <div className="validation-check-list">
            {props.result.json_body.validationResults.map((result, index) =>
              result.checks.map((check, checkIndex) => (
                <div key={`${index}${checkIndex}`}>
                  <i
                    className={`${props.fn.getSuccessTextClass(
                      check.success,
                    )} material-icons material-icons-font md-theme-default`}
                    title={props.fn.getSuccessMessage(check.success)}
                    style={{ marginRight: '3px' }}
                  >
                    {props.fn.getSuccessIcon(check.success)}
                  </i>
                  {props.fn.getOptionName(check)}
                </div>
              )),
            )}
          </div>
        </div>
      }
    >
      <span>
        <i
          className={`${props.fn.getSuccessTextClass(
            props.result.json_body.summary.success,
          )} material-icons material-icons-font md-theme-default`}
        >
          {props.fn.getSuccessIcon(props.result.json_body.summary.success)}
        </i>
        <span
          className="link-status-summary-text"
          style={{ fontSize: 12, color: colors.gray.medium, marginLeft: '5px' }}
        >
          {props.result.json_body.summary.optionSuccesses.length}/
          {props.result.json_body.summary.totalNumOptions} {t`checks passed`}
        </span>
      </span>
    </HtmlTooltip>
  </div>
)

/*
  For when people demand to have the refresh button on the read table.
            <i
                className="material-icons material-icons-font link-refresh"
                style={{fontSize: '20px !important', position: 'absolute', top:'50%', right: '4px', marginTop: '-12px', cursor: 'pointer'}}
                data-url={props.linkValue}
                data-field-id={props.linkGovernance.field_id}
                data-row-num={props.rowNum}
            >
                refresh
            </i>
*/

const ErrorTemplate = props => (
  <div style="position:relative; padding-right: 32px; word-break:break-word;">
    <i
      className="material-icons material-icons-font md-theme-default text-danger"
      style="font-size: 20px !important;"
    >
      {t`warning`}
    </i>
    <span style="font-size:12px; color: rgba(0,0,0,.35); margin-right: 24px;">
      {t`Link validation temporarily unavailable.`}
    </span>
    <i
      className="material-icons material-icons-font link-refresh"
      style="font-size: 20px !important; position:absolute; top: 50%; right: 4px; margin-top: -12px; cursor: pointer;"
      data-url={props.linkValue}
      data-field-id={props.linkGovernance.field_id}
      data-row-num={props.rowNum}
    >
      {t`refresh`}
    </i>
  </div>
)

const Template403 = () => (
  <div style={{ position: 'relative', paddingRight: 32, wordBreak: 'break-word' }}>
    <i
      className="material-icons material-icons-font md-theme-default text-danger"
      style={{ fontSize: '20px !important' }}
    >
      {t`warning`}
    </i>
    <span style={{ fontSize: 12, color: 'rgba(0,0,0,.35)', marginRight: 24 }}>
      {t`Link validation blocked.`}
    </span>
    <p>
      {t`Claravine Link Validators have been blocked by the servers responding to the request. Please reach out to your Claravine CSM on steps to take to whitelist Claravine link validators so that your links can be successfully validated.`}
    </p>
  </div>
)

const PendingTemplate = () => (
  <div>
    <HtmlTooltip
      style={{ backgroundColor: 'white' }}
      title={
        <div className="statusWrapper tooltipWrapper">
          <div className="statusPopover validationTooltip arrow-top">
            <h3>{t`Validating page...`}</h3>
            <p>
              {t`We're currently validating your page. Hang tight for just a few seconds while we gather the results...`}
            </p>
          </div>
        </div>
      }
    >
      <span>
        <i className="material-icons material-icons-font md-theme-default text-secondary icon-spin">
          {t`data_usage`}
        </i>
      </span>
    </HtmlTooltip>
  </div>
)

const NoDataTemplate = () => (
  <div>
    <span>{t`Link verification status was not recorded`}</span>
  </div>
)

export const LinkStatus = ({ urlAudits, linkValue, linkGovernance, rowNum }) => {
  if (urlAudits.length === 0 && window.location.pathname.includes('reports')) {
    return <NoDataTemplate />
  }

  if (urlAudits.length === 0 || isPendingOrInProgress(urlAudits)) {
    return <PendingTemplate />
  }

  if (is403(urlAudits)) {
    return <Template403 />
  }

  // if blank, then nothing to render
  if (isTemporaryError(urlAudits)) {
    return <ErrorTemplate linkValue={linkValue} linkGovernance={linkGovernance} />
  }

  if (
    urlAudits[0].json_body &&
    !urlAudits[0].json_body.summary &&
    (urlAudits[0].status === 3 || urlAudits[0].status === 4)
  ) {
    return <NoDataTemplate />
  }

  // else if it makes it here render the standard template
  return (
    <RootTemplate
      result={urlAudits[0]}
      linkValue={linkValue}
      linkGovernance={linkGovernance}
      rowNum={rowNum}
      fn={{
        getSuccessMessage,
        getSuccessIcon,
        getSuccessTextClass,
        getValidationName,
        getOptionName,
        getTooltipHeader,
        getTooltipDescription,
      }}
    />
  )
}

function is403(urlAudits) {
  return (
    urlAudits[0].json_body &&
    urlAudits[0].json_body.rawResponse &&
    urlAudits[0].json_body.rawResponse.status == 403
  )
}

function isPendingOrInProgress(urlAudits) {
  return urlAudits.length === 1 && (urlAudits[0].status === 1 || urlAudits[0].status === 2)
}

function isTemporaryError(urlAudits) {
  const hasError = urlAudits.length === 1 && urlAudits[0].error
  const errorMessage = urlAudits[0].json_body.errorMessage || ''

  // todo: switch these to error codes instead of pivoting on the message
  const exceededLimit = urlAudits.length === 1 && errorMessage.indexOf('limit exceeded') !== -1
  const noResources =
    urlAudits.length === 1 && errorMessage.indexOf('available validation resource') !== -1

  return hasError && (exceededLimit || noResources)
}

function getTooltipHeader(resultSummary) {
  return resultSummary.success ? t`Landing page success` : t`Landing page error`
}

function getTooltipDescription(resultSummary) {
  return resultSummary.success
    ? t`All of your checks passed correctly. Looks like you are good to go!`
    : t`It looks like some of your checks failed. You may want to contact your Site Owner to ensure your site is configured correctly.`
}

function getSuccessMessage(success) {
  return success ? t`Success` : t`Failed`
}

function getSuccessIcon(success) {
  return success ? 'check_circle' : 'error'
}

function getSuccessTextClass(success) {
  return success ? 'text-success' : 'text-danger'
}

function getOptionName(option) {
  const OPTION_NAMES = {
    page_exists: t`Page exists`,
    aa_pixel_exists: t`Adobe Analytics pixel exists`,
    aa_correct_report_suite: t`Correct report suite`,
    aa_correct_report: t`Correct report`,
    ga_pixel_exists: t`Google Analytics pixel exists`,
    ga_correct_property: t`Correct property`,
    fb_pixel_exists: t`Facebook Pixel exists`,
    fb_pixel_correct_id: t`Facebook Pixel correct ID`,
  }

  return OPTION_NAMES[option.optionSlug]
}

function getValidationName(validation) {
  return validation.validationTypeName
}
